import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TownshipPublicSettings } from '../interfaces';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { doc, getDoc } from 'firebase/firestore';
import { getEnvName, getTownshipImage, updateTheme } from '../globals';
import { db, functions } from '../app.component';
import { httpsCallable } from 'firebase/functions';
import { ReCaptchaV3Service } from 'ng-recaptcha';

@Component({
  selector: 'app-saldo-checker',
  templateUrl: './saldo-checker.component.html',
  styleUrls: ['./saldo-checker.component.scss'],
  standalone: false,
})
export class SaldoCheckerComponent implements OnInit {
  env = environment;
  awaitingResponse: boolean;
  loaded: boolean = false;
  townshipId: string;
  township: TownshipPublicSettings;
  saldoCheckerForm: UntypedFormGroup;

  environmentObj: any;
  error: string;
  headerImg: string;

  resultString: string;
  resultAmount?: number;
  resultDate: string;
  showValue: boolean = false;
  showRemaining: boolean = false;
  showDate: boolean = false;

  originalValue: string;
  value: string;

  townshipImage: any;

  @ViewChild('voucherInput', { static: false }) voucherInput: ElementRef;

  constructor(
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private recaptchaV3Service: ReCaptchaV3Service,
  ) {}

  ngOnInit(): void {
    this.saldoCheckerForm = this.fb.group({
      voucher: [, Validators.required],
    });
    this.translate.setDefaultLang(environment.language);
    this.translate.use(environment.language);
    this.route.params.subscribe(async (params) => {
      this.townshipId = params.townshipId;
      this.township = (
        await getDoc(doc(db, `township/${this.townshipId}/settings/public`))
      ).data() as TownshipPublicSettings;

      this.environmentObj = getEnvName(this.township.voucherPrefix);
      this.townshipImage = getTownshipImage(this.township);
      updateTheme({
        primary: this.township.primaryColor,
        secondary: this.township.accentColor,
      });
      this.loaded = true;
    });
  }

  getError(name) {
    const field = this.saldoCheckerForm.get(name);
    if (field.touched || !field.pristine) {
      let error;

      if (field.hasError('required')) {
        error = 'required';
      }
      if (field.hasError('unknown_voucher')) {
        error = 'unknown_voucher';
      }
      return this.translate.instant(`errors.${error}`);
    }
  }

  async reset() {
    this.value = null;
    this.originalValue = null;
    this.resultString = null;
    this.showValue = false;
    this.showRemaining = false;
    this.showDate = false;
    this.saldoCheckerForm.reset();
    setTimeout(() => {
      this.voucherInput.nativeElement.click();
    });
  }

  async checkSaldo() {
    this.resultDate = null;
    this.resultString = null;
    this.awaitingResponse = true;
    this.recaptchaV3Service.execute('importantAction').subscribe(async (token: string) => {
      if (this.saldoCheckerForm.valid && token) {
        const callable = httpsCallable(functions, 'httpCheckSaldo');
        const result = await callable({
          voucherNumber: this.saldoCheckerForm.value.voucher,
          townshipId: this.townshipId,
        });
        if (result?.data) {
          switch (result.data['type']) {
            case 'claimed':
            case 'partiallyClaimed':
            case 'paid':
              this.showValue = true;
              this.originalValue = result.data['originalValue'];
              this.value = '0';

              if (
                result.data['type'] === 'partiallyClaimed' &&
                result.data['originalValue'] - result.data['value'] > 0
              ) {
                this.value = (result.data['originalValue'] - result.data['value']).toString();
                this.showRemaining = true;
                this.showDate = true;
              } else {
                this.resultString = this.getTranslation('claimed');
              }

              if (result.data['validUntilDate'] && result.data['validUntilDate'] !== 'onbekend') {
                this.resultDate = this.getTranslation('validUntilDate', [
                  result.data['validUntilDate'],
                ]);
              } else {
                this.resultDate = this.getTranslation('noUntilValidDate');
              }
              break;
            case 'expired':
              this.showDate = true;
              this.resultString = this.getTranslation('expired');
              if (result.data['validUntilDate'] && result.data['validUntilDate'] !== 'onbekend') {
                this.resultDate = this.getTranslation('validUntilDate', [
                  result.data['validUntilDate'],
                ]);
              } else {
                this.resultDate = this.getTranslation('noUntilValidDate');
              }
              break;
            case 'activated':
              this.showValue = true;
              this.showRemaining = true;
              this.showDate = true;
              this.originalValue = result.data['originalValue']
                ? result.data['originalValue']
                : result.data['value'];
              this.value = result.data['value'];
              if (result.data['validUntilDate'] && result.data['validUntilDate'] !== 'onbekend') {
                this.resultDate = this.getTranslation('validUntilDate', [
                  result.data['validUntilDate'],
                ]);
              } else {
                this.resultDate = this.getTranslation('noUntilValidDate');
              }
              break;
            case 'unknown_voucher':
              // this.resultString = this.getTranslation(result.data['type']);
              this.saldoCheckerForm.controls.voucher.setErrors({
                unknown_voucher: true,
              });
              break;
            case 'not_used':
              this.resultString = this.getTranslation(result.data['type']);
              break;
            default:
              this.resultString = this.getTranslation('notAvailable');
              break;
          }

          this.value = this.value ? Number.parseFloat(this.value).toFixed(2) : null;
          this.originalValue = Number.parseFloat(this.originalValue).toFixed(2);
          if (!this.value) {
            this.voucherInput.nativeElement.click();
          }
          this.awaitingResponse = false;
        }
      }
    });
  }

  getTranslation(objName, values?): string {
    let newObj = {};
    if (values) {
      if (values.length === 2) {
        newObj = {
          value: values[0],
          amount: values[1],
        };
      } else if (values.length === 1) {
        newObj = {
          value: values[0],
        };
      }
    }
    const message = this.translate.instant(`saldo-checker.messages.${objName}`, newObj) as string;
    return message;
  }
}
